/**
 * VerifyStep — Profile setup / verification screen (mock)
 *
 * First name, last name, handicap, handicap system, home club search.
 */

import { useState } from 'preact/hooks'
import type { VerifyFormData } from '../hooks/useBookingState'

interface VerifyStepProps {
  initialData?: Partial<VerifyFormData>
  onVerify: (data: VerifyFormData) => void
  clubName: string
}

const HANDICAP_SYSTEMS = ['Golf Australia', 'USGA', 'CONGU', 'EGA', 'Other']

export function VerifyStep({ initialData, onVerify, clubName }: VerifyStepProps) {
  const [form, setForm] = useState<VerifyFormData>({
    firstName: initialData?.firstName || 'James',
    lastName: initialData?.lastName || 'Anderson',
    handicap: initialData?.handicap || '12.4',
    handicapSystem: initialData?.handicapSystem || 'Golf Australia',
    homeClubName: initialData?.homeClubName || 'Kingston Heath',
    homeClubCountry: initialData?.homeClubCountry || 'Australia',
  })
  const [errors, setErrors] = useState<Partial<Record<keyof VerifyFormData, string>>>({})

  const updateField = (field: keyof VerifyFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof VerifyFormData, string>> = {}
    if (!form.firstName.trim()) newErrors.firstName = 'First name is required'
    if (!form.lastName.trim()) newErrors.lastName = 'Last name is required'
    if (!form.homeClubName.trim()) newErrors.homeClubName = 'Home club is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (validate()) {
      onVerify(form)
    }
  }

  return (
    <div className="bw2-step-verify" role="form" aria-label="Complete your profile">
      <div className="bw2-verify__header">
        <h2 className="bw2-verify__title" id="bw2-verify-heading">Complete Your Profile</h2>
        <div className="bw2-verify__subtitle">
          {clubName} requires visitor verification
        </div>
      </div>

      <div className="bw2-verify__form">
        {/* Name row */}
        <div className="bw2-verify__row">
          <div className="bw2-verify__field">
            <label className="bw2-verify__label" htmlFor="bw2-verify-firstName">First Name *</label>
            <input
              id="bw2-verify-firstName"
              type="text"
              className={`bw2-verify__input ${errors.firstName ? 'bw2-verify__input--error' : ''}`}
              value={form.firstName}
              onInput={(e) => updateField('firstName', (e.target as HTMLInputElement).value)}
              placeholder="James"
              aria-required="true"
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? 'bw2-verify-firstName-error' : undefined}
              autoComplete="given-name"
            />
            {errors.firstName && (
              <div className="bw2-verify__error" id="bw2-verify-firstName-error" role="alert">
                {errors.firstName}
              </div>
            )}
          </div>
          <div className="bw2-verify__field">
            <label className="bw2-verify__label" htmlFor="bw2-verify-lastName">Last Name *</label>
            <input
              id="bw2-verify-lastName"
              type="text"
              className={`bw2-verify__input ${errors.lastName ? 'bw2-verify__input--error' : ''}`}
              value={form.lastName}
              onInput={(e) => updateField('lastName', (e.target as HTMLInputElement).value)}
              placeholder="Anderson"
              aria-required="true"
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? 'bw2-verify-lastName-error' : undefined}
              autoComplete="family-name"
            />
            {errors.lastName && (
              <div className="bw2-verify__error" id="bw2-verify-lastName-error" role="alert">
                {errors.lastName}
              </div>
            )}
          </div>
        </div>

        {/* Handicap row */}
        <div className="bw2-verify__row">
          <div className="bw2-verify__field">
            <label className="bw2-verify__label" htmlFor="bw2-verify-handicap">Handicap</label>
            <input
              id="bw2-verify-handicap"
              type="number"
              step="0.1"
              className="bw2-verify__input"
              value={form.handicap}
              onInput={(e) => updateField('handicap', (e.target as HTMLInputElement).value)}
              placeholder="12.4"
            />
          </div>
          <div className="bw2-verify__field">
            <label className="bw2-verify__label" htmlFor="bw2-verify-handicapSystem">System</label>
            <select
              id="bw2-verify-handicapSystem"
              className="bw2-verify__select"
              value={form.handicapSystem}
              onChange={(e) => updateField('handicapSystem', (e.target as HTMLSelectElement).value)}
            >
              {HANDICAP_SYSTEMS.map(sys => (
                <option key={sys} value={sys}>{sys}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Home Club */}
        <div className="bw2-verify__field">
          <label className="bw2-verify__label" htmlFor="bw2-verify-homeClub">Home Club *</label>
          <input
            id="bw2-verify-homeClub"
            type="text"
            className={`bw2-verify__input ${errors.homeClubName ? 'bw2-verify__input--error' : ''}`}
            value={form.homeClubName}
            onInput={(e) => updateField('homeClubName', (e.target as HTMLInputElement).value)}
            placeholder="Search for your home club..."
            aria-required="true"
            aria-invalid={!!errors.homeClubName}
            aria-describedby={errors.homeClubName ? 'bw2-verify-homeClub-error' : 'bw2-verify-homeClub-hint'}
          />
          {errors.homeClubName && (
            <div className="bw2-verify__error" id="bw2-verify-homeClub-error" role="alert">
              {errors.homeClubName}
            </div>
          )}
          {form.homeClubName && !errors.homeClubName && (
            <div className="bw2-verify__club-hint" id="bw2-verify-homeClub-hint">
              🔍 In production, this would search the club database
            </div>
          )}
        </div>

        {/* Country */}
        <div className="bw2-verify__field">
          <label className="bw2-verify__label" htmlFor="bw2-verify-country">Country</label>
          <input
            id="bw2-verify-country"
            type="text"
            className="bw2-verify__input"
            value={form.homeClubCountry}
            onInput={(e) => updateField('homeClubCountry', (e.target as HTMLInputElement).value)}
            placeholder="Australia"
            autoComplete="country-name"
          />
        </div>
      </div>

      <button
        className="bw2-verify__submit-btn"
        onClick={handleSubmit}
        type="button"
      >
        Continue to Booking →
      </button>

      <div className="bw2-verify__mock-notice" aria-hidden="true">
        ⏳ Mock Mode — No real verification
      </div>
    </div>
  )
}
